import React from 'react';

import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';

import { ActionButton } from 'components/Button/ActionButton';
import { MaxShelfLifeEditModal } from 'components/recipes/MaxShelfLifeEditModal';
import { LabelValueProperties } from 'components/recipes/sidePane/LabelValueProperties';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useCoreApi } from 'hooks/useApi';

import { showAsyncModal } from 'utils/asyncModal';
import { pluralize } from 'utils/formatting';
import { isMaxShelfLifeEnabled } from 'utils/tenantSettings';

/**
 * Item-level settings (one value per item, shared by all locations), shown beneath the item/location settings in
 * the Planning and Purchasing side panes. Renders nothing unless the Max Shelf Life tenant setting is enabled.
 */
export function ItemSettingsSection({
  itemUid,
  itemCode,
  onSaved,
}: {
  itemUid: string;
  itemCode: string;
  /** Called after a successful save, e.g. to reload the pane's search row */
  onSaved?: () => void;
}) {
  const maxShelfLifeEnabled = isMaxShelfLifeEnabled();

  const {
    data: itemSettingsData,
    isLoading: itemSettingsIsLoading,
    error: itemSettingsError,
    reload: reloadItemSettings,
  } = useCoreApi(schemas.items.getItemSettings, maxShelfLifeEnabled ? { pathParams: { itemUid } } : null);

  if (!maxShelfLifeEnabled) {
    return null;
  }

  const maxShelfLifeDays = itemSettingsData?.maxShelfLifeDays;

  return (
    <>
      <br />
      <div
        className={css`
          font-weight: 600;
        `}
      >
        <InfoTooltip useInfoIcon title="These settings apply to this item at every location">
          Item settings
        </InfoTooltip>
      </div>
      <LabelValueProperties
        properties={[
          {
            label: (
              <InfoTooltip title="Limits how many days of supply Recurrency will recommend purchasing">
                Max Shelf Life
              </InfoTooltip>
            ),
            value: itemSettingsIsLoading ? (
              <LoadingOutlined />
            ) : itemSettingsError ? (
              // Don't offer Edit: the modal would open empty and could overwrite a limit we failed to load
              <>
                Failed to load
                {` | `}
                <ActionButton label="Retry" onClick={reloadItemSettings} />
              </>
            ) : (
              <>
                {maxShelfLifeDays != null ? pluralize(maxShelfLifeDays, 'day', 'days', true) : '—'}
                {` | `}
                <ActionButton
                  dataTestId="edit-max-shelf-life-button"
                  label={
                    <>
                      <EditOutlined /> Edit
                    </>
                  }
                  onClick={() => {
                    showAsyncModal(MaxShelfLifeEditModal, {
                      itemUid,
                      itemCode,
                      initialMaxShelfLifeDays: maxShelfLifeDays,
                      showAllLocationsWarning: true,
                      onSaved: () => {
                        reloadItemSettings();
                        onSaved?.();
                      },
                    });
                  }}
                />
              </>
            ),
          },
        ]}
      />
    </>
  );
}
